import { Component } from "@theme/component";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLElement} addToCartButtonContainer - Sticky bar's button component container
 * @property {HTMLButtonElement} buyNowButton - Buy now submit button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = [
    "stickyBar",
    "addToCartButtonContainer",
    "buyNowButton",
  ];

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {MutationObserver | null} */
  #priceSyncObserver = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    this.#setupTargetButton();
    this.#showStickyBar();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    target?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupPriceSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    this.#priceSyncObserver?.disconnect();
  }

  /**
   * Finds the main product form button so the sticky bar can reuse the existing add-to-cart flow.
   */
  #setupTargetButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    this.#targetAddToCartButton = productForm.querySelector('[data-add-to-cart-role="main"]');
  }

  // Public action handlers
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    if (this.#targetAddToCartButton.disabled) return;

    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector("sticky-add-to-cart");
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute("data-stuck") || "false";
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute("data-stuck", currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[data-add-to-cart-role="main"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#updateBuyNowForm();
    this.#setupPriceSync();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = "";
    const variantTitleElement = this.querySelector(".sticky-add-to-cart__variant");
    const productId = this.dataset.productId;
    const variantPicker = this.closest(".shopify-section")?.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll("input:checked"))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== "")
      .join(" / ");
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
    this.#updateBuyNowForm();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    stickyBar.dataset.stuck = "true";
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest(".shopify-section");
    if (!sectionElement) return null;

    return sectionElement.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  #getProductPrice() {
    const productId = this.dataset.productId;
    const sectionElement = this.closest(".shopify-section");
    if (!productId || !sectionElement) return null;

    return sectionElement.querySelector(`product-price[data-product-id="${CSS.escape(productId)}"]`);
  }

  #getStickyPrice() {
    return this.refs.stickyBar.querySelector(".sticky-add-to-cart__price");
  }

  #getStickyAddToCartButton() {
    const buttonContainer = this.refs.addToCartButtonContainer;
    if (!buttonContainer) return null;

    return (
      buttonContainer.refs?.addToCartButton ??
      buttonContainer.querySelector('[ref="addToCartButton"]')
    );
  }

  #getQuantityDisplay() {
    const buttonContainer = this.refs.addToCartButtonContainer;
    if (!buttonContainer) return null;

    return buttonContainer.querySelector('[ref="quantityDisplay"]');
  }

  #getQuantityNumber() {
    const buttonContainer = this.refs.addToCartButtonContainer;
    if (!buttonContainer) return null;

    return buttonContainer.querySelector('[ref="quantityNumber"]');
  }

  #setupPriceSync() {
    this.#priceSyncObserver?.disconnect();

    const productPrice = this.#getProductPrice();
    if (!productPrice) return;

    this.#syncStickyPriceFromProductPrice();

    this.#priceSyncObserver = new MutationObserver(() => {
      this.#syncStickyPriceFromProductPrice();
    });

    this.#priceSyncObserver.observe(productPrice, {
      attributes: true,
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  #syncStickyPriceFromProductPrice() {
    const productPrice = this.#getProductPrice();
    const stickyPrice = this.#getStickyPrice();
    if (!productPrice || !stickyPrice) return;

    const sourcePriceContainer = productPrice.querySelector('[ref="priceContainer"]');
    const stickyPriceContainer = stickyPrice.querySelector('[ref="priceContainer"]');

    if (sourcePriceContainer && stickyPriceContainer) {
      stickyPriceContainer.replaceWith(sourcePriceContainer.cloneNode(true));
    } else if (sourcePriceContainer) {
      stickyPrice.replaceChildren(sourcePriceContainer.cloneNode(true));
    }

    const sourceVolumePricingNote = productPrice.querySelector('[ref="volumePricingNote"]');
    const stickyVolumePricingNote = stickyPrice.querySelector('[ref="volumePricingNote"]');

    if (sourceVolumePricingNote && stickyVolumePricingNote) {
      stickyVolumePricingNote.replaceWith(sourceVolumePricingNote.cloneNode(true));
    } else if (sourceVolumePricingNote) {
      stickyPrice.appendChild(sourceVolumePricingNote.cloneNode(true));
    } else {
      stickyVolumePricingNote?.remove();
    }
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || "1") || 1;
    this.#updateButtonText();
    this.#updateBuyNowForm();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const addToCartButton = this.#getStickyAddToCartButton();
    const quantityDisplay = this.#getQuantityDisplay();
    const quantityNumber = this.#getQuantityNumber();

    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = "inline";
    } else {
      quantityDisplay.style.display = "none";
    }
  }

  #updateBuyNowForm() {
    const { buyNowButton } = this.refs;
    const addToCartButton = this.#getStickyAddToCartButton();
    const variantId = this.dataset.currentVariantId || "";

    if (!addToCartButton) return;

    buyNowButton.disabled = addToCartButton.disabled || variantId === "";
  }
}

if (!customElements.get("sticky-add-to-cart")) {
  customElements.define("sticky-add-to-cart", StickyAddToCartComponent);
}
